import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, DollarSign, TrendingUp, Calculator, Calendar, Search, Filter } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { API_BASE } from '../../utils/constants';
import LoadingSpinner from '../ui/LoadingSpinner';
import QuickStats from '../ui/QuickStats';
const Dashboard = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all'); // all, active, settled
  const [stats, setStats] = useState({
    totalGroups: 0,
    totalExpenses: 0,
    totalOwed: 0,
    totalLent: 0
  });
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadGroups = async () => {
    try {
      const res = await fetch(`${API_BASE}/groups/user/email/${user.email}`);
      if (!res.ok) throw new Error('Failed to fetch groups');
      
      const groupData = await res.json();
      console.log('Loaded user groups:', groupData);
      setGroups(Array.isArray(groupData) ? groupData : []);
      
      // Calculate stats (frontend-only)
      await calculateStats(groupData);
    } catch (err) {
      console.error('Failed to load groups', err);
      setGroups([]);
      alert('Failed to load your groups. Please try refreshing the page.');
    }
    setLoading(false);
  };

  const calculateStats = async (groupData) => {
    try {
      const userRes = await fetch(`${API_BASE}/users/email/${user.email}`);
      const userData = await userRes.json();
      
      let totalOwed = 0;
      let totalLent = 0;
      let totalExpenses = 0;

      // Fetch details for each group to calculate stats
      for (const group of groupData) {
        try {
          const detailRes = await fetch(`${API_BASE}/groups/${group.id}/details?userId=${userData.id}`);
          if (detailRes.ok) {
            const details = await detailRes.json();
            totalExpenses += details.expenses?.length || 0;
            
            // Parse balance strings to calculate owed/lent amounts
            details.balances?.forEach(balance => {
              const oweMatch = balance.match(/You owe .+ ₹([\d.]+)/);
              const owedMatch = balance.match(/(.+) owes you ₹([\d.]+)/);
              
              if (oweMatch) {
                totalOwed += parseFloat(oweMatch[1]);
              } else if (owedMatch) {
                totalLent += parseFloat(owedMatch[2]);
              }
            });
          }
        } catch (err) {
          console.warn(`Failed to load details for group ${group.id}`);
        }
      }

      setStats({
        totalGroups: groupData.length,
        totalExpenses,
        totalOwed,
        totalLent
      });
    } catch (err) {
      console.error('Failed to calculate stats', err);
    }
  };

  useEffect(() => {
    if (user && user.email) {
      loadGroups();
    }
  }, [user]);

  const handleGroupClick = (groupId) => {
    navigate(`/group/${groupId}`);
  };

  // Filter and search groups
  const getFilteredGroups = () => {
    let filtered = groups;

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(group =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const filteredGroups = getFilteredGroups();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Welcome Back!</h1>
            <p className="mt-2 text-gray-600">Here's your expense overview</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Today</p>
            <p className="text-lg font-semibold text-gray-900">
              {new Date().toLocaleDateString('en-IN', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Groups</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalGroups}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Calculator className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Expenses</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalExpenses}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">You Owe</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.totalOwed)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-teal-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-teal-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">You're Owed</p>
              <p className="text-2xl font-bold text-teal-600">{formatCurrency(stats.totalLent)}</p>
            </div>
          </div>
        </div>
      </div>
    <div className="mb-8">
      <QuickStats userId={user?.email} />
    </div>
      {/* Search and Filter */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search groups by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Groups</option>
              <option value="active">Active</option>
              <option value="settled">Settled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Groups Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Your Groups</h2>
          <span className="text-sm text-gray-500">
            {filteredGroups.length} of {groups.length} groups
          </span>
        </div>

        {filteredGroups.length === 0 ? (
          <div className="text-center py-12">
            {searchTerm ? (
              <>
                <Search className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No groups found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No groups match your search "{searchTerm}"
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 text-teal-600 hover:text-teal-700 text-sm"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No groups yet</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by creating your first group.</p>
                <button
                  onClick={() => navigate('/create-group')}
                  className="mt-6 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700"
                >
                  <Plus className="-ml-1 mr-2 h-5 w-5" />
                  Create Group
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGroups.map((group) => (
                <div
                  key={group.id}
                  className="group relative bg-gray-50 hover:bg-white border border-gray-200 hover:border-teal-300 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-md"
                  onClick={() => handleGroupClick(group.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-teal-400 to-blue-500 rounded-lg flex items-center justify-center">
                        <Users className="h-6 w-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors">
                          {group.name}
                        </h3>
                        <p className="text-sm text-gray-500">Group ID: {group.id}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <button className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-teal-700 bg-teal-100 hover:bg-teal-200 transition-colors">
                      View Details
                    </button>
                  </div>

                  {/* Hover indicator */}
                  <div className="absolute inset-0 rounded-lg ring-2 ring-teal-500 ring-opacity-0 group-hover:ring-opacity-100 transition-all duration-200 pointer-events-none"></div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/create-group')}
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 shadow-sm transition-colors"
                >
                  <Plus className="-ml-1 mr-2 h-5 w-5" />
                  Create New Group
                </button>
                <button
                  onClick={() => navigate('/add-expense')}
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-teal-700 bg-teal-100 hover:bg-teal-200 shadow-sm transition-colors"
                >
                  <DollarSign className="-ml-1 mr-2 h-5 w-5" />
                  Add Expense
                </button>
                <button
                  onClick={() => navigate('/transactions')}
                  className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors"
                >
                  <Calendar className="-ml-1 mr-2 h-5 w-5" />
                  View Transactions
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { Users, Plus, DollarSign, TrendingUp, Calculator, Calendar, Search, Filter } from 'lucide-react';
// import { useAuth } from '../auth/AuthProvider';
// import { API_BASE } from '../../utils/constants';
// import LoadingSpinner from '../ui/LoadingSpinner';
// import QuickStats from '../ui/QuickStats';

// const Dashboard = () => {
//   const [groups, setGroups] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [filterActive, setFilterActive] = useState('all');
//   const [stats, setStats] = useState({
//     totalGroups: 0,
//     totalExpenses: 0,
//     totalOwed: 0,
//     totalLent: 0
//   });
//   const { user } = useAuth();
//   const navigate = useNavigate();

//   const loadGroups = async () => {
//     try {
//       const res = await fetch(`${API_BASE}/groups/user/email/${user.email}`);
//       if (!res.ok) throw new Error('Failed to fetch groups');
      
//       const groupData = await res.json();
//       console.log('Loaded user groups:', groupData);
//       setGroups(Array.isArray(groupData) ? groupData : []);
      
//       // Calculate stats
//       await calculateStats(groupData);
//     } catch (err) {
//       console.error('Failed to load groups', err);
//       setGroups([]);
//       if (window.showNotification) {
//         window.showNotification('error', 'Failed to load your groups. Please try refreshing the page.');
//       }
//     }
//     setLoading(false);
//   };

//   const calculateStats = async (groupData) => {
//     try {
//       const userRes = await fetch(`${API_BASE}/users/email/${user.email}`);
//       const userData = await userRes.json();
      
//       let totalOwed = 0;
//       let totalLent = 0;
//       let totalExpenses = 0;

//       // Fetch details for each group to calculate stats
//       for (const group of groupData) {
//         try {
//           const detailRes = await fetch(`${API_BASE}/groups/${group.id}/details?userId=${userData.id}`);
//           if (detailRes.ok) {
//             const details = await detailRes.json();
//             totalExpenses += details.expenses?.length || 0;
            
//             // Parse balance strings to calculate owed/lent amounts
//             details.balances?.forEach(balance => {
//               const oweMatch = balance.match(/You owe .+ ₹([\d.]+)/);
//               const owedMatch = balance.match(/(.+) owes you ₹([\d.]+)/);
              
//               if (oweMatch) {
//                 totalOwed += parseFloat(oweMatch[1]);
//               } else if (owedMatch) {
//                 totalLent += parseFloat(owedMatch[2]);
//               }
//             });
//           }
//         } catch (err) {
//           console.warn(`Failed to load details for group ${group.id}`);
//         }
//       }

//       setStats({
//         totalGroups: groupData.length,
//         totalExpenses,
//         totalOwed,
//         totalLent
//       });
//     } catch (err) {
//       console.error('Failed to calculate stats', err);
//     }
//   };

//   useEffect(() => {
//     if (user && user.email) {
//       console.log('Loading groups for user:', user.email);
//       loadGroups();
//     }
//   }, [user]);

//   const handleGroupClick = (groupId) => {
//     console.log('Navigating to group:', groupId);
//     navigate(`/group/${groupId}`);
//   };

//   // Filter and search groups
//   const getFilteredGroups = () => {
//     let filtered = groups;

//     // Apply search filter
//     if (searchTerm.trim()) {
//       filtered = filtered.filter(group =>
//         group.name.toLowerCase().includes(searchTerm.toLowerCase())
//       );
//     }

//     return filtered;
//   };

//   const formatCurrency = (amount) => {
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 0,
//       maximumFractionDigits: 0
//     }).format(amount);
//   };

//   if (loading) {
//     return <LoadingSpinner />;
//   }

//   const filteredGroups = getFilteredGroups();

//   return (
//     <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
//       {/* Welcome Section */}
//       <div className="mb-8">
//         <div className="flex flex-col md:flex-row md:items-center justify-between">
//           <div>
//             <h1 className="text-3xl font-bold text-gray-900">Welcome Back! 👋</h1>
//             <p className="mt-2 text-gray-600">Here's your expense overview for today</p>
//             <p className="text-sm text-gray-400">Logged in as: {user?.email}</p>
//           </div>
//           <div className="mt-4 md:mt-0 text-left md:text-right">
//             <p className="text-sm text-gray-500">Today</p>
//             <p className="text-lg font-semibold text-gray-900">
//               {new Date().toLocaleDateString('en-IN', { 
//                 weekday: 'long', 
//                 year: 'numeric', 
//                 month: 'long', 
//                 day: 'numeric' 
//               })}
//             </p>
//           </div>
//         </div>
//       </div>

//       {/* Stats Cards */}
//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
//         <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
//           <div className="flex items-center">
//             <div className="p-3 bg-blue-100 rounded-lg">
//               <Users className="h-6 w-6 text-blue-600" />
//             </div>
//             <div className="ml-4">
//               <p className="text-sm font-medium text-gray-600">Total Groups</p>
//               <p className="text-2xl font-bold text-gray-900">{stats.totalGroups}</p>
//             </div>
//           </div>
//         </div>

//         <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
//           <div className="flex items-center">
//             <div className="p-3 bg-green-100 rounded-lg">
//               <Calculator className="h-6 w-6 text-green-600" />
//             </div>
//             <div className="ml-4">
//               <p className="text-sm font-medium text-gray-600">Total Expenses</p>
//               <p className="text-2xl font-bold text-gray-900">{stats.totalExpenses}</p>
//             </div>
//           </div>
//         </div>

//         <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
//           <div className="flex items-center">
//             <div className="p-3 bg-red-100 rounded-lg">
//               <TrendingUp className="h-6 w-6 text-red-600" />
//             </div>
//             <div className="ml-4">
//               <p className="text-sm font-medium text-gray-600">You Owe</p>
//               <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.totalOwed)}</p>
//             </div>
//           </div>
//         </div>

//         <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
//           <div className="flex items-center">
//             <div className="p-3 bg-teal-100 rounded-lg">
//               <DollarSign className="h-6 w-6 text-teal-600" />
//             </div>
//             <div className="ml-4">
//               <p className="text-sm font-medium text-gray-600">You're Owed</p>
//               <p className="text-2xl font-bold text-teal-600">{formatCurrency(stats.totalLent)}</p>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Quick Stats Component */}
//       <QuickStats userId={user?.email} />

//       {/* Search and Filter */}
//       <div className="mb-6">
//         <div className="flex flex-col sm:flex-row gap-4">
//           <div className="flex-1 relative">
//             <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
//             <input
//               type="text"
//               placeholder="Search groups by name..."
//               className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//             />
//           </div>
          
//           <div className="flex items-center space-x-2">
//             <Filter className="h-5 w-5 text-gray-400" />
//             <select
//               value={filterActive}
//               onChange={(e) => setFilterActive(e.target.value)}
//               className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
//             >
//               <option value="all">All Groups</option>
//               <option value="active">Active</option>
//               <option value="settled">Settled</option>
//             </select>
//           </div>
//         </div>
//       </div>

//       {/* Groups Section */}
//       <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
//         <div className="flex items-center justify-between mb-6">
//           <h2 className="text-xl font-semibold text-gray-900">Your Groups</h2>
//           <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
//             {filteredGroups.length} of {groups.length} groups
//           </span>
//         </div>

//         {filteredGroups.length === 0 ? (
//           <div className="text-center py-12">
//             {searchTerm ? (
//               <>
//                 <Search className="mx-auto h-12 w-12 text-gray-400" />
//                 <h3 className="mt-2 text-sm font-medium text-gray-900">No groups found</h3>
//                 <p className="mt-1 text-sm text-gray-500">
//                   No groups match your search "{searchTerm}"
//                 </p>
//                 <button
//                   onClick={() => setSearchTerm('')}
//                   className="mt-4 text-teal-600 hover:text-teal-700 text-sm font-medium"
//                 >
//                   Clear search
//                 </button>
//               </>
//             ) : (
//               <>
//                 <Users className="mx-auto h-12 w-12 text-gray-400" />
//                 <h3 className="mt-2 text-sm font-medium text-gray-900">No groups yet</h3>
//                 <p className="mt-1 text-sm text-gray-500">Get started by creating your first group.</p>
//                 <button
//                   onClick={() => navigate('/create-group')}
//                   className="mt-6 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 transition-colors"
//                 >
//                   <Plus className="-ml-1 mr-2 h-5 w-5" />
//                   Create Group
//                 </button>
//               </>
//             )}
//           </div>
//         ) : (
//           <>
//             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//               {filteredGroups.map((group) => (
//                 <div
//                   key={group.id}
//                   className="group relative bg-gradient-to-br from-gray-50 to-white hover:from-teal-50 hover:to-blue-50 border border-gray-200 hover:border-teal-300 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-lg transform hover:-translate-y-1"
//                   onClick={() => handleGroupClick(group.id)}
//                 >
//                   <div className="flex items-start justify-between">
//                     <div className="flex items-center space-x-3">
//                       <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-teal-400 to-blue-500 rounded-lg flex items-center justify-center shadow-sm">
//                         <Users className="h-6 w-6 text-white" />
//                       </div>
//                       <div className="min-w-0 flex-1">
//                         <h3 className="text-lg font-semibold text-gray-900 group-hover:text-teal-700 transition-colors truncate">
//                           {group.name}
//                         </h3>
//                         <p className="text-sm text-gray-500">ID: {group.id}</p>
//                       </div>
//                     </div>
//                   </div>
                  
//                   <div className="mt-4">
//                     <button className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-teal-700 bg-teal-100 hover:bg-teal-200 transition-colors group-hover:bg-teal-200">
//                       View Details →
//                     </button>
//                   </div>

//                   {/* Hover glow effect */}
//                   <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-teal-400 to-blue-400 opacity-0 group-hover:opacity-10 transition-opacity duration-200 pointer-events-none"></div>
//                 </div>
//               ))}
//             </div>

//             {/* Quick Actions */}
//             <div className="mt-8 pt-6 border-t border-gray-200">
//               <div className="flex flex-col sm:flex-row gap-4">
//                 <button
//                   onClick={() => navigate('/create-group')}
//                   className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 shadow-sm transition-colors"
//                 >
//                   <Plus className="-ml-1 mr-2 h-5 w-5" />
//                   Create New Group
//                 </button>
//                 <button
//                   onClick={() => navigate('/add-expense')}
//                   className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-teal-700 bg-teal-100 hover:bg-teal-200 shadow-sm transition-colors"
//                 >
//                   <DollarSign className="-ml-1 mr-2 h-5 w-5" />
//                   Add Expense
//                 </button>
//                 <button
//                   onClick={() => navigate('/transactions')}
//                   className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors"
//                 >
//                   <Calendar className="-ml-1 mr-2 h-5 w-5" />
//                   View Transactions
//                 </button>
//               </div>
//             </div>
//           </>
//         )}
//       </div>
//     </div>
//   );
// };

// export default Dashboard;